import React, { memo } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';

interface ProfileFormFieldsProps {
  profile: {
    name: string;
    first_name: string;
    last_name: string;
    x_username: string;
    bio: string;
    mobile_number?: string;
    metro_card_number: string;
    vehicle_number: string;
  };
  isEditing: boolean;
  onNameChange: (value: string) => void;
  onXUsernameChange: (value: string) => void;
  onBioChange: (value: string) => void;
  onMobileChange: (value: string) => void;
  onMetroCardChange: (value: string) => void;
  onVehicleNumberChange: (value: string) => void;
}

export const ProfileFormFields = memo(({
  profile,
  isEditing,
  onNameChange,
  onXUsernameChange,
  onBioChange,
  onMobileChange,
  onMetroCardChange,
  onVehicleNumberChange,
}: ProfileFormFieldsProps) => {
  return (
    <div className="flex-1 space-y-4 w-full">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 w-full">
        <div className="space-y-2 w-full">
          <Label htmlFor="name" className="text-foreground">Full Name</Label>
          <Input 
            id="name" 
            name="name" 
            type="text"
            value={profile.name} 
            onChange={(e) => onNameChange(e.target.value)} 
            disabled={!isEditing} 
            placeholder="Enter your full name" 
            maxLength={30} 
            autoComplete="name"
            className="w-full border-border focus:border-primary focus:ring-0 bg-background text-foreground" 
          />
        </div>
        
        <div className="space-y-2 w-full">
          <Label htmlFor="x_username" className="text-foreground">X (Twitter) Username</Label>
          <div className="relative w-full">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">@</span>
            <Input 
              id="x_username" 
              name="x_username" 
              type="text"
              value={profile.x_username} 
              onChange={(e) => onXUsernameChange(e.target.value)} 
              disabled={!isEditing} 
              className="pl-8 w-full border-border focus:border-primary focus:ring-primary bg-background text-foreground" 
              placeholder="Enter your X (Twitter) username" 
              maxLength={30} 
              autoComplete="username"
            />
          </div>
        </div>
        
        <div className="space-y-2 w-full">
          <Label htmlFor="mobile_number" className="text-foreground">Mobile Number</Label>
          <Input
            id="mobile_number"
            name="mobile_number"
            type="tel"
            value={profile.mobile_number || ''}
            onChange={(e) => {
              if (!isEditing) return;
              const value = e.target.value.replace(/\D/g, "");
              onMobileChange(value);
            }}
            disabled={!isEditing}
            placeholder="Enter your mobile number"
            className="w-full border-border focus:border-primary focus:ring-0 bg-background text-foreground"
            maxLength={15}
            inputMode="numeric"
            pattern="\\d*"
            autoComplete="tel"
          />
        </div>
      </div>
      
      <div className="space-y-2 w-full">
        <Label htmlFor="bio" className="text-foreground">Bio</Label>
        <Textarea 
          id="bio" 
          name="bio" 
          value={profile.bio} 
          onChange={(e) => onBioChange(e.target.value)} 
          disabled={!isEditing} 
          rows={3} 
          placeholder="Share public info like bio, insta id, or a way to contact you" 
          maxLength={200} 
          autoComplete="off"
          className="w-full border-border focus:border-primary focus:ring-primary bg-background text-foreground" 
        />
      </div>

      <div className="space-y-2 w-full">
        <Label htmlFor="metro_card_number" className="text-foreground">Metro Card Number</Label>
        <Input 
          id="metro_card_number" 
          name="metro_card_number" 
          type="text"
          value={profile.metro_card_number} 
          onChange={(e) => {
            if (!isEditing) return;
            const value = e.target.value.replace(/\D/g, "");
            onMetroCardChange(value);
          }}
          disabled={!isEditing} 
          placeholder="Enter your metro card number" 
          maxLength={16} 
          minLength={4} 
          inputMode="numeric" 
          pattern="\\d*" 
          autoComplete="off"
          className="border-border focus:border-primary focus:ring-primary bg-background text-foreground" 
        />
      </div>

      <div className="space-y-2 w-full">
        <Label htmlFor="vehicle_number" className="text-foreground">Vehicle Number</Label>
        <Input 
          id="vehicle_number" 
          name="vehicle_number" 
          type="text"
          value={profile.vehicle_number} 
          onChange={(e) => {
            if (!isEditing) return;
            const value = e.target.value.replace(/[^A-Za-z0-9\s]/g, "").toUpperCase();
            onVehicleNumberChange(value);
          }}
          disabled={!isEditing} 
          placeholder="Enter your vehicle registration number" 
          maxLength={15} 
          minLength={6}
          autoComplete="off"
          className="border-border focus:border-primary focus:ring-primary bg-background text-foreground" 
        />
      </div>
    </div>
  );
});

ProfileFormFields.displayName = 'ProfileFormFields';

